import React from 'react';
import PropTypes from 'prop-types';
import {action, computed, observable} from 'mobx';
import {inject, observer} from 'mobx-react';
import autobind from 'autobind-decorator';

import {withI18n} from 'spa/Components/Context';

import Button, {
  ButtonSizeTypes,
  ButtonThemeTypes,
  ButtonType,
} from '@urbanmassage/component-library/lib/Button/Button';
import Divider from '@urbanmassage/component-library/lib/Divider/Divider';
import Filter from '@urbanmassage/component-library/lib/Icons/Filter/Filter';
import RatingsSummary from '@urbanmassage/component-library/lib/RatingsSummary/RatingsSummary';
import ReviewsFiltersSidebar from 'spa/Components/Reviews/ReviewsFiltersSidebar';
import SidePanel, { SlideAnimationStatus } from '@urbanmassage/component-library/lib/SidePanel/SidePanel';
import TextReviews from 'spa/Components/Reviews/TextReviews';
import Typography, {TypeVariants} from '@urbanmassage/component-library/lib/Typography/Typography';

import 'spa/Components/Reviews/worker-reviews-long.less';

import {
  MAX_STAR_REVIEW,
  MIN_RATING_COUNTER,
  MIN_STAR_REVIEW,
  RECENT_SORT_VALUE,
} from 'spa/Components/Reviews/values';

@inject(({
  store: {
    tracker,
  },
  therapistsState,
}) => ({
  therapistsState,
  tracker,
}))
@withI18n
@observer
class WorkerReviewsLong extends React.Component {
  static propTypes = {
    isMobile: PropTypes.bool,
    showPinnedReviews: PropTypes.bool,
    textReviewsEnabled: PropTypes.bool,
    therapistsState: PropTypes.shape({
      filtersApplied: PropTypes.bool,
      fullReviewsList: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.object),
        PropTypes.object,
      ]).isRequired,
      offset: PropTypes.number.isRequired,
      reviewsList: PropTypes.object,
      selectedId: PropTypes.string.isRequired,
      sortReviews: PropTypes.string.isRequired,
      specialitiesFilter: PropTypes.number,
      workerDetails: PropTypes.shape({
        instance: PropTypes.shape({
          id: PropTypes.number,
        }),
      }),
      workerPromotedReviewsList: PropTypes.arrayOf(PropTypes.object),
      workerRating: PropTypes.string,
      workerRatingsBreakdown: PropTypes.object,
      totalReviews: PropTypes.number.isRequired,
    }).isRequired,
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
    }).isRequired,
  }

  static defaultProps = {
    isMobile: false,
    showPinnedReviews: false,
    textReviewsEnabled: true,
  }

  @observable isAnimatingPanel = false;
  @observable reviewsFiltersPanelStatus = SlideAnimationStatus.CLOSED;

  @autobind
  @action('onClickOpenFilters - WorkerReviewsLong')
  onClickOpenFilters() {
    const {
      therapistsState: {
        selectedId,
      },
      tracker,
    } = this.props;

    this.isAnimatingPanel = true;

    this.reviewsFiltersPanelStatus = SlideAnimationStatus.SLIDE_IN;

    tracker.track('Opened review filters', {
      category: 'Workers',
      label: 'Reviews',
      worker: {
        id: selectedId,
      },
    });
  }

  @autobind
  @action('onFiltersPanelStatusChange - WorkerReviewsLong')
  onFiltersPanelStatusChange(status) {
    if (status === this.reviewsFiltersPanelStatus) {
      this.isAnimatingPanel = false;
    }
  }

  @autobind
  @action('onClickClearFilters - WorkerReviewsLong')
  onClickClearFilters() {
    const {
      therapistsState,
    } = this.props;

    therapistsState.filtersApplied = false;
    therapistsState.sortReviews = RECENT_SORT_VALUE;
    therapistsState.specialitiesFilter = null;
    therapistsState.offset = 0;
  }

  @autobind
  @action('onCloseReviewsFiltersContextual - WorkerReviewsLong')
  onCloseReviewsFiltersContextual(event) {
    if (event) event.preventDefault();

    const {
      therapistsState: {
        selectedId,
      },
      tracker,
    } = this.props;

    tracker.track('Closed reviews filter panel', {
      category: 'Workers',
      label: 'Reviews',
      worker: {
        id: selectedId,
      },
    });

    this.isAnimatingPanel = true;

    this.reviewsFiltersPanelStatus = SlideAnimationStatus.SLIDE_OUT;
  }

  @computed get ratingCount() {
    const {workerRatingsBreakdown} = this.props.therapistsState;

    if (!workerRatingsBreakdown) return null;

    const counter = workerRatingsBreakdown.reduce((tempCounter, rating) => {
      return rating.rating ? tempCounter + rating.count : tempCounter;
    }, 0);

    return counter;
  }

  @computed get showRatings() {
    const {ratingCount} = this;
    if (!ratingCount) return null;

    return ratingCount >= MIN_RATING_COUNTER;
  }

  @computed get reviewsTitle() {
    const {ratingCount, showRatings} = this;
    const {totalReviews} = this.props.therapistsState;
    const {i18n} = this.context;

    if (!showRatings && !totalReviews) return null;

    //rating histogram won't be shown, but we need a title for text reviews
    if (!showRatings) {
      return totalReviews === 1 ?
        i18n._x('1 Review', 'Therapist Reviews') :
        i18n._x('%(totalReviews)s Reviews', 'Therapist Reviews', {
          totalReviews,
        });
    }

    return i18n._x('%(ratingCount)s Ratings', 'Therapist Reviews', {
      ratingCount,
    });
  }

  @computed get reviewsCount() {
    const {therapistsState: {workerRatingsBreakdown}} = this.props;
    if (!workerRatingsBreakdown) return null;

    if (!workerRatingsBreakdown) {
      return {
        5: 0,
        4: 0,
        3: 0,
        2: 0,
        1: 0,
      };
    }

    const reviewsList = {};

    for (let i = MIN_STAR_REVIEW; i <= MAX_STAR_REVIEW; i++) {
      const starRating = workerRatingsBreakdown.find(ratings => ratings.rating === i);

      reviewsList[i] = starRating ? (starRating.count || 0) : 0;
    }

    return reviewsList;
  }

  @computed get reviewsList() {
    const {i18n} = this.context;
    const {
      textReviewsEnabled,
      therapistsState: {
        fullReviewsList,
        reviewsList,
      },
    } = this.props;

    if (textReviewsEnabled && fullReviewsList && fullReviewsList.length) {
      return (
        <TextReviews
          reviews={fullReviewsList}
        />
      );
    }

    if (reviewsList && reviewsList.isLoading) {
      return (
        <Typography align="center" margin="2rem 0 1.6rem" theme="mediumBody" variant={TypeVariants.body}>
          {i18n._x('Loading...', 'Therapist reviews list')}
        </Typography>
      );
    }

    if (fullReviewsList && !fullReviewsList.length) {
      return (
        <Typography align="center" margin="2rem 0 1.6rem" theme="mediumBody" variant={TypeVariants.body}>
          {i18n._x('No matching reviews found', 'Therapist reviews list')}
        </Typography>
      );
    }

    return null;
  }

  @computed get promotedReviewsList() {
    const {
      showPinnedReviews,
      therapistsState: {
        workerDetails,
        workerPromotedReviewsList,
      },
    } = this.props;

    if (workerPromotedReviewsList && showPinnedReviews) {
      return (
        <div className="worker-reviews-long__pinned-reviews-container">
          <TextReviews
            bordered
            reviews={workerPromotedReviewsList}
            smallStars
            workerId={workerDetails && workerDetails.instance && workerDetails.instance.id}
          />
          <div className="worker-reviews-long__divider">
            <Divider
              marginBottom="0"
              marginTop="0"
              size="12"
            />
          </div>
        </div>
      );
    }

    return null;
  }

  @computed get filtersControls() {
    const {i18n} = this.context;
    const {
      isMobile,
      therapistsState: {
        filtersApplied,
      },
    } = this.props;
    const {onClickClearFilters, onClickOpenFilters, reviewsList} = this;

    if (!reviewsList) return null;

    return (
      <div className="worker-reviews-long__reviews-filters-button-container">
        {filtersApplied && (
          <div className="worker-reviews-long__reviews-clear-filters-button-container">
            <Button
              margin="0 0 2rem 0"
              onClick={onClickClearFilters}
              size={ButtonSizeTypes.small}
              theme={ButtonThemeTypes.transparent}
              type={ButtonType.button}
            >
              {i18n._x('Clear filters', 'Therapist reviews filters title')}
            </Button>
          </div>
        )}
        <Button
          onClick={onClickOpenFilters}
          preIcon={!isMobile && <Filter />}
          size={ButtonSizeTypes.medium}
          theme={ButtonThemeTypes.grey}
          type={ButtonType.button}
        >
          {!isMobile ?
            i18n._x('Filters', 'Therapist reviews filters title') :
            <Filter />
          }
        </Button>
      </div>
    );
  }

  render() {
    const {
      therapistsState: {
        workerRating,
      },
    } = this.props;

    const {
      filtersControls,
      isAnimatingPanel,
      onFiltersPanelStatusChange,
      onCloseReviewsFiltersContextual,
      promotedReviewsList,
      reviewsTitle,
      showRatings,
      reviewsCount,
      reviewsList,
    } = this;
    const {i18n} = this.context;

    return (
      <div className="worker-reviews-long">
        {filtersControls}
        {showRatings ? (
          <RatingsSummary
            averageReviewText={i18n._x('%(totalRating)s out of 5', 'Therapist Reviews', {
              totalRating: workerRating,
            })}
            ratingCounts={[
              reviewsCount[5],
              reviewsCount[4],
              reviewsCount[3],
              reviewsCount[2],
              reviewsCount[1],
            ]}
            totalReviewsText={reviewsTitle}
          />
        ) : (
          <Typography variant={TypeVariants.h3} className="worker-reviews-long__title">
            {reviewsTitle}
          </Typography>
        )}
        {promotedReviewsList}
        {reviewsList && (
          <div className="worker-reviews-long__list-container">
            {reviewsList}
          </div>
        )}
        <SidePanel
          closedClassName="reviews-filters-side-panel--closed"
          onClose={onCloseReviewsFiltersContextual}
          onStatusChangeEnd={onFiltersPanelStatusChange}
          openClassName="reviews-filters-side-panel--open"
          panelStatus={this.reviewsFiltersPanelStatus}
        >
          <ReviewsFiltersSidebar
            isAnimatingPanel={isAnimatingPanel}
            onClose={onCloseReviewsFiltersContextual}
          />
        </SidePanel>
      </div>
    );
  }
}

export default WorkerReviewsLong;



// WEBPACK FOOTER //
// ./spa/Components/Reviews/WorkerReviewsLong.jsx